import type { FormBuilderField } from "../../types/formBuilder";
import type { FioParts } from "../../types/fio";
import { formatFioLine, parseFio } from "./fio";
import { resolveFieldType } from "./normalizeFieldType";
import { formatDateRangeDayCount, parseDateRangeValue } from "./dateRangeField";
import { readLabeledChoiceValue } from "./fieldOptions";
import {
  formatDottedDate,
  formatTableDateValue,
  parseAnswerDate,
} from "../dates/formatAnswerDate";
import {
  ageFromBirthdate,
  formatAgeLabel,
  formatValueWithAge,
  parseCalendarDate,
} from "./dateField";

export type FormatSubmissionValueOptions = {
  /** id → name для полей city, если подписи нет в field.cityLabels. */
  cityNames?: Record<string, string>;
  /** Возраст на created_at, ключ — name поля. */
  ages?: Record<string, number> | null;
  /** Дата ответа для пересчёта возраста, если ages нет. */
  asOf?: string | Date | null;
};

function stringifyRaw(raw: unknown): string {
  if (raw == null) return "";
  if (typeof raw === "string") return raw;
  if (typeof raw === "number" || typeof raw === "boolean") return String(raw);
  try {
    return JSON.stringify(raw);
  } catch {
    return String(raw);
  }
}

function isFioShape(value: unknown): value is Partial<FioParts> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return (
    "last_name" in record || "first_name" in record || "patronymic" in record
  );
}

function fioPartsFromShape(value: unknown): FioParts | null {
  if (!isFioShape(value)) return null;
  const record = value as Partial<FioParts>;
  return {
    last_name: String(record.last_name ?? "").trim(),
    first_name: String(record.first_name ?? "").trim(),
    patronymic: String(record.patronymic ?? "").trim(),
  };
}

function parseCityIdKey(raw: unknown, text: string): string | null {
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return String(Math.trunc(raw));
  }

  const trimmed = text.trim();
  if (/^\d+$/.test(trimmed)) return trimmed;
  return null;
}

function resolveCityName(
  cityId: string,
  field: FormBuilderField | undefined,
  cityNames?: Record<string, string>,
): string | null {
  const fromField = field?.cityLabels?.[cityId]?.trim();
  if (fromField) return fromField;

  const fromCatalog = cityNames?.[cityId]?.trim();
  if (fromCatalog) return fromCatalog;

  return null;
}

function resolveBirthdateAge(
  field: FormBuilderField,
  raw: unknown,
  options?: FormatSubmissionValueOptions,
): number | null {
  const fromAges =
    field.name && options?.ages ? options.ages[field.name] : undefined;
  if (typeof fromAges === "number" && Number.isFinite(fromAges)) {
    return Math.trunc(fromAges);
  }
  return ageFromBirthdate(raw, options?.asOf);
}

function extractFioParts(raw: unknown, text: string): FioParts | null {
  const fromRaw = fioPartsFromShape(raw);
  if (fromRaw) return fromRaw;

  if (!text.startsWith("{")) return null;

  try {
    return fioPartsFromShape(JSON.parse(text));
  } catch {
    return null;
  }
}

export function formatSubmissionValue(
  field: FormBuilderField | undefined,
  raw: unknown,
  options?: FormatSubmissionValueOptions,
): string {
  const labeled = readLabeledChoiceValue(raw);
  if (labeled) return labeled.label;

  const text = stringifyRaw(raw).trim();
  if (!text) return "—";

  if (field && resolveFieldType(field.type) === "city") {
    const cityId = parseCityIdKey(raw, text);
    if (cityId) {
      const name = resolveCityName(cityId, field, options?.cityNames);
      if (name) return name;
    }
  }

  const autoFio = extractFioParts(raw, text);
  if (field?.type === "fio" || autoFio) {
    const parts = autoFio ?? parseFio(text);
    return formatFioLine(parts) || "—";
  }

  if (field?.type === "privacy") {
    return text === "1" ? "Да" : "—";
  }

  if (field && resolveFieldType(field.type) === "birthdate") {
    const formatted = formatTableDateValue(text, false) ?? text;
    return formatValueWithAge(
      formatted,
      resolveBirthdateAge(field, raw, options),
    );
  }

  if (field && resolveFieldType(field.type) === "date") {
    const withTime =
      Boolean(field.withTime) || /T\d{2}:\d{2}|\s\d{2}:\d{2}/.test(text);
    return formatTableDateValue(text, withTime) ?? text;
  }

  if (field && resolveFieldType(field.type) === "date-range") {
    const range = parseDateRangeValue(raw);
    if (range?.start && range.end) {
      const withTime =
        Boolean(field.withTime) || /T\d{2}:\d{2}/.test(range.start);
      const start = formatTableDateValue(range.start, withTime) ?? range.start;
      const end = formatTableDateValue(range.end, withTime) ?? range.end;
      const duration = formatDateRangeDayCount(range);
      return `${start} — ${end}${duration ? ` (${duration})` : ""}`;
    }
  }

  if (text.startsWith("[") && text.endsWith("]")) {
    try {
      const parsed = JSON.parse(text) as unknown;
      if (Array.isArray(parsed)) {
        const items = parsed
          .map((item) => stringifyRaw(item).trim())
          .filter(Boolean);
        if (items.length > 0) return items.join(", ");
      }
    } catch {
      /* keep raw text */
    }
  }

  return text;
}

/** Значение для группировки и аналитики: у birthdate — возраст, у date — календарный день. */
export function formatSubmissionGroupValue(
  field: FormBuilderField | undefined,
  raw: unknown,
  options?: FormatSubmissionValueOptions,
): string {
  if (field && resolveFieldType(field.type) === "birthdate") {
    const age = resolveBirthdateAge(field, raw, options);
    return age == null ? "—" : formatAgeLabel(age);
  }
  if (field && resolveFieldType(field.type) === "date") {
    const parsed =
      parseAnswerDate(stringifyRaw(raw).trim()) ??
      parseCalendarDate(stringifyRaw(raw).trim());
    return parsed
      ? formatDottedDate(parsed)
      : formatSubmissionValue(field, raw, options);
  }
  return formatSubmissionValue(field, raw, options);
}
